import React, { MutableRefObject, useMemo, useRef, useState } from 'react';
import { groupBy, partition } from 'lodash';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { Collapse, NodeGroup, EASING } from '@fiverr-private/transition';
import { I18n } from '@fiverr-private/i18n-react';
import { useBriefContext } from '../../../../common/BriefContext';
import { Chip, isAttachmentsSection } from '../../../common/Chips';
import { BriefSection } from '../../../../types';
import { getNearestOpacity } from '../../util';
import { useIncludeSection } from '../useIncludeSection';
import { DEFAULT_SECTIONS_GROUP } from '../constants';

const STARTING_DELAY = 300;

const AnimatedChipGroup = ({
  sections,
  includeSection,
  delayIndexRef,
}: {
  sections: BriefSection[];
  includeSection: (section: BriefSection) => void;
  delayIndexRef: MutableRefObject<number>;
}) => {
  const [isFirstRender, setIsFirstRender] = useState(true);

  return (
    <NodeGroup
      data={sections}
      keyAccessor={(d) => d.title}
      start={() => ({ opacity: [isFirstRender ? 0 : 1], x: [isFirstRender ? -24 : 0] })}
      enter={(_, index) => {
        const currentIndex = delayIndexRef.current;

        if (!sections[index]?.included) {
          delayIndexRef.current = delayIndexRef.current + 1;
        }

        return {
          opacity: [1],
          x: [0],
          timing: {
            duration: 200,
            delay: currentIndex * 50 + STARTING_DELAY,
            ease: EASING.easeIn,
          },
        };
      }}
    >
      {(nodes) => (
        <Container display="flex" direction="column">
          {nodes.map(({ data: section, key, state: { opacity, x } }) => (
            <Container
              transform={`translate(${x}px, 0)`}
              opacity={getNearestOpacity(opacity)}
              marginBottom="3"
              key={key}
            >
              <Collapse duration={'fast1'} endingHeight={40} onAnimationEnd={() => setIsFirstRender(false)}>
                <Container>
                  <Chip
                    key={key}
                    title={key}
                    onClick={() => {
                      includeSection(section);
                      delayIndexRef.current = 0;
                    }}
                  />
                </Container>
              </Collapse>
            </Container>
          ))}
        </Container>
      )}
    </NodeGroup>
  );
};

interface ExcludedSectionsListProps {
  sections: BriefSection[];
}

export const ExcludedSectionsList = ({ sections }: ExcludedSectionsListProps) => {
  const delayIndexRef = useRef<number>(-1);
  const { editedBrief } = useBriefContext();
  const includeSection = useIncludeSection();

  const [attachmentsSections, briefSections] = useMemo(() => partition(sections, isAttachmentsSection), [sections]);

  if (!editedBrief) {
    return null;
  }

  const sectionGroups: Record<string, BriefSection[]> = groupBy(
    briefSections,
    (section) => section.group ?? DEFAULT_SECTIONS_GROUP
  );

  const { [DEFAULT_SECTIONS_GROUP]: rootGroupSections = [], ...otherGroups } = sectionGroups;

  const groups: [string, BriefSection[]][] = [
    [DEFAULT_SECTIONS_GROUP, rootGroupSections],
    ...Object.entries(otherGroups),
    ['attachments', attachmentsSections],
  ];

  return (
    <Stack direction="column" gap="4">
      {groups.map(([groupId, sections]) => (
        <Stack direction="column" key={groupId}>
          <Text size="b_xs" color="bodySecondary">
            <I18n k={`ai_brief.edit_brief.${groupId}`} />
          </Text>
          <AnimatedChipGroup sections={sections} includeSection={includeSection} delayIndexRef={delayIndexRef} />
        </Stack>
      ))}
    </Stack>
  );
};
